import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/shared/Sidebar';
import PageLoading from '../../../components/shared/PageLoading';
import Button from '../../../components/shared/Button';
import StatusBadge from '../../../components/shared/StatusBadge';
import { getCSRFToken } from '../../../utils/api';

/**
 * USOKIE Platform IP Object Detail View
 * Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
 */
const IpObjectDetail = ({ ipObjectId }) => {
  const [ipObject, setIpObject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchIpObject();
  }, [ipObjectId]);

  const fetchIpObject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/ip_objects/${ipObjectId}`);
      const data = await response.json();
      setIpObject(data);
    } catch (error) {
      console.error('Error fetching IP object:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this IP object?')) return;

    try {
      const response = await fetch(`/api/v1/ip_objects/${ipObjectId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCSRFToken()
        }
      });

      if (response.ok) {
        window.location.href = '/ip_objects';
      } else {
        alert('Error deleting IP object');
      }
    } catch (error) {
      console.error('Error deleting IP object:', error);
      alert('Error deleting IP object');
    }
  };

  if (loading) {
    return <PageLoading message="Loading Asset Data" />;
  }

  if (!ipObject) {
    return (
      <div className="flex h-screen bg-black">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-[rgba(255,251,247,0.5)]">IP object not found</p>
            <Button href="/ip_objects" variant="secondary" className="mt-4">
              Back to Registry
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-black">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-8">
          {/* Document Header */}
          <div className="mb-8 pb-6 border-b border-[rgba(255,251,247,0.2)]">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <span
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  ASSET IP.{ipObject.id}
                </span>
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold text-[#fffbf7] tracking-[0.08em] uppercase">
                    {ipObject.name}
                  </h1>
                  <StatusBadge status={ipObject.status} />
                </div>
              </div>
              <div className="flex gap-3">
                <Button href={`/ip_objects/${ipObject.id}/edit`}>
                  Edit
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
                <Button href="/ip_objects" variant="secondary">
                  Back to List
                </Button>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    01
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Basic Information
                  </span>
                </div>
              </div>
              <div className="p-6">
                <dl className="grid grid-cols-[200px_1fr] gap-4">
                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Name
                  </dt>
                  <dd className="text-sm text-[#fffbf7]">{ipObject.name}</dd>

                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Description
                  </dt>
                  <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                    {ipObject.description || 'No description provided'}
                  </dd>

                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Category
                  </dt>
                  <dd className="text-sm text-[rgba(255,251,247,0.6)]">{ipObject.category?.name}</dd>

                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Status
                  </dt>
                  <dd className="text-sm text-[rgba(255,251,247,0.6)] capitalize">{ipObject.status}</dd>
                </dl>
              </div>
            </div>

            {/* Owners Section */}
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    02
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Owners ({ipObject.owners?.length || 0})
                  </span>
                </div>
              </div>
              <div className="p-6">
                {ipObject.owners && ipObject.owners.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ipObject.owners.map((owner) => (
                      <div
                        key={owner.id}
                        className="border border-[rgba(255,251,247,0.1)] bg-black p-4 hover:border-[rgba(255,251,247,0.2)] transition-colors"
                      >
                        <h3 className="text-sm font-semibold text-[#fffbf7] mb-1">
                          <a
                            href={`/parties/${owner.id}`}
                            className="hover:text-[#a1c6a2] transition-colors"
                          >
                            {owner.name}
                          </a>
                        </h3>
                        <span
                          className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          {owner.party_type?.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[rgba(255,251,247,0.4)] italic">No owners assigned</p>
                )}
              </div>
            </div>

            {/* Rights Categories Section */}
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    03
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Available Rights Categories ({ipObject.available_rights_categories?.length || 0})
                  </span>
                </div>
              </div>
              <div className="p-6">
                {ipObject.available_rights_categories && ipObject.available_rights_categories.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ipObject.available_rights_categories.map((category) => (
                      <div
                        key={category.id}
                        className="border border-[rgba(255,251,247,0.1)] bg-black p-4"
                      >
                        <h4 className="text-sm font-semibold text-[#fffbf7] mb-1">{category.name}</h4>
                        {category.description && (
                          <p className="text-xs text-[rgba(255,251,247,0.4)]">{category.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[rgba(255,251,247,0.4)] italic">No rights categories assigned</p>
                )}
              </div>
            </div>

            {/* Metadata Section */}
            {ipObject.metadata && Object.keys(ipObject.metadata).length > 0 && (
              <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
                <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                  <div className="flex items-center gap-4">
                    <span
                      className="text-xs text-[#a1c6a2] tracking-wider"
                      style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                    >
                      04
                    </span>
                    <span
                      className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                      style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                    >
                      Metadata
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <pre
                    className="text-xs text-[rgba(255,251,247,0.6)] bg-black border border-[rgba(255,251,247,0.1)] p-4 overflow-x-auto"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    {JSON.stringify(ipObject.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Timestamps Section */}
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    {ipObject.metadata && Object.keys(ipObject.metadata).length > 0 ? '05' : '04'}
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Timestamps
                  </span>
                </div>
              </div>
              <div className="p-6">
                <dl className="grid grid-cols-[200px_1fr] gap-4">
                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Created
                  </dt>
                  <dd
                    className="text-sm text-[rgba(255,251,247,0.6)]"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    {new Date(ipObject.created_at).toLocaleString()}
                  </dd>

                  <dt
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Last Updated
                  </dt>
                  <dd
                    className="text-sm text-[rgba(255,251,247,0.6)]"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    {new Date(ipObject.updated_at).toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>

            {/* History Section */}
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    {ipObject.metadata && Object.keys(ipObject.metadata).length > 0 ? '06' : '05'}
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Change History
                  </span>
                </div>
              </div>
              <div className="p-6">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="px-4 py-2 border border-[rgba(255,251,247,0.2)] text-[rgba(255,251,247,0.6)] text-[10px] tracking-[0.15em] uppercase hover:text-[#fffbf7] hover:border-[rgba(255,251,247,0.4)] transition-colors"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  {showHistory ? 'Hide' : 'Show'} History
                </button>

                {showHistory && (
                  <div className="mt-4 p-4 border border-[rgba(255,251,247,0.1)] bg-black">
                    <p className="text-sm text-[rgba(255,251,247,0.5)]">
                      Change history is tracked via PaperTrail. View audit logs for detailed version history.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default IpObjectDetail;
